import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';

import { getParticipant, getSubmission } from '../../api/participantApi';
import { getAnalysis, reanalyze }        from '../../api/analysisApi';
import Badge        from '../../components/ui/Badge';
import Card         from '../../components/ui/Card';
import Spinner      from '../../components/ui/Spinner';
import ScoreGauge   from '../../components/ui/ScoreGauge';
import ProgressBar  from '../../components/ui/ProgressBar';

// ── Helpers ───────────────────────────────────────────────────────────────────
function statusVariant(status) {
  const map = {
    submitted: 'success',
    draft:     'warning',
    pending:   'warning',
    analyzed:  'primary',
    started:   'info',
  };
  return map[status] ?? 'default';
}

function statusLabel(status) {
  const map = {
    submitted: 'Submitted',
    draft:     'In Progress',
    analyzed:  'Analyzed',
  };
  return map[status] ?? status;
}

function confidenceVariant(conf) {
  if (!conf) return 'default';
  const c = (conf + '').toLowerCase();
  if (c === 'high')   return 'success';
  if (c === 'medium') return 'warning';
  return 'error';
}

function scoreBarColor(score) {
  if (score >= 80) return 'success';
  if (score >= 60) return 'secondary';
  if (score >= 40) return 'accent';
  return 'error';
}

function capitalize(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ── Dimension Score Row ───────────────────────────────────────────────────────
function DimensionRow({ name, data }) {
  const [showRationale, setShowRationale] = useState(false);
  const score      = data?.score      ?? 0;
  const confidence = data?.confidence ?? null;
  const rationale  = data?.rationale  ?? null;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate text-sm font-medium text-neutral-700">{capitalize(name)}</span>
          {confidence && (
            <Badge variant={confidenceVariant(confidence)} className="shrink-0 text-[10px]">
              {confidence}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-heading text-sm font-semibold text-neutral-800">{score}</span>
          {rationale && (
            <button
              onClick={() => setShowRationale((v) => !v)}
              className="rounded p-0.5 text-neutral-400 hover:text-neutral-600 transition"
              title="Toggle rationale"
            >
              {showRationale
                ? <ChevronUpIcon className="h-3.5 w-3.5" />
                : <ChevronDownIcon className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
      <ProgressBar value={score} color={scoreBarColor(score)} />
      {showRationale && rationale && (
        <p className="rounded-input bg-neutral-50 px-3 py-2 text-xs text-neutral-500 leading-relaxed">
          {rationale}
        </p>
      )}
    </div>
  );
}

// ── Q&A Accordion Item ────────────────────────────────────────────────────────
function QAItem({ index, question, answer, feedback }) {
  const [open, setOpen] = useState(false);

  const scoreColor =
    !feedback          ? '' :
    feedback.score >= 80 ? 'bg-success-100 text-success-700' :
    feedback.score >= 60 ? 'bg-secondary-100 text-secondary-700' :
    feedback.score >= 40 ? 'bg-warning-100 text-warning-700' :
                           'bg-error-100 text-error-700';

  return (
    <div className="border-b border-neutral-100 last:border-0">
      <button
        className="flex w-full items-start justify-between gap-4 py-3 text-left transition hover:bg-neutral-50"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 shrink-0 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">
            Q{index}
          </span>
          <span className="text-sm font-medium text-neutral-800 leading-relaxed">
            {question}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {feedback?.score != null && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${scoreColor}`}>
              {feedback.score}/100
            </span>
          )}
          {open
            ? <ChevronUpIcon className="h-4 w-4 text-neutral-400" />
            : <ChevronDownIcon className="h-4 w-4 text-neutral-400" />}
        </div>
      </button>

      {open && (
        <div className="pb-4 pl-9 space-y-3">
          <p className="text-sm text-neutral-600 leading-relaxed">
            {answer ?? <span className="italic text-neutral-400">No answer provided</span>}
          </p>

          {feedback && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-3 text-xs">
              {feedback.feedback && (
                <p className="text-neutral-600 leading-relaxed">{feedback.feedback}</p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {feedback.what_was_good && (
                  <div className="rounded-md border border-success-200 bg-success-50 p-3">
                    <p className="mb-1 font-semibold text-success-700">What was good</p>
                    <p className="text-success-800 leading-relaxed">{feedback.what_was_good}</p>
                  </div>
                )}
                {feedback.what_to_improve && (
                  <div className="rounded-md border border-warning-200 bg-warning-50 p-3">
                    <p className="mb-1 font-semibold text-warning-700">What to improve</p>
                    <p className="text-warning-800 leading-relaxed">{feedback.what_to_improve}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ParticipantDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [participant, setParticipant] = useState(null);
  const [submission, setSubmission]   = useState(null);
  const [analysis, setAnalysis]       = useState(null);
  const [answers, setAnswers]         = useState([]);

  const [loading, setLoading]                 = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError]                     = useState(null);
  const [reanalyzing, setReanalyzing]         = useState(false);

  const fetchAll = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const pRes = await getParticipant(id);
      const p    = pRes.data.data ?? pRes.data;
      setParticipant(p);

      const submissions = p.submissions ?? [];
      const latestSub   = submissions[0] ?? null;
      const subId       = latestSub?.submission_id ?? p.submission_id ?? null;

      if (subId) {
        const sRes = await getSubmission(subId);
        const s    = sRes.data.data ?? sRes.data;
        setSubmission(s);
        setAnswers(s.answers ?? []);

        setAnalysisLoading(true);
        getAnalysis(subId)
          .then((aRes) => setAnalysis(aRes.data.data ?? aRes.data))
          .catch(() => setAnalysis(null))
          .finally(() => setAnalysisLoading(false));
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load participant data');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [id]);

  const handleReanalyze = async () => {
    const subId = submission?.submission_id;
    if (!subId) return;
    setReanalyzing(true);
    setAnalysisLoading(true);
    try {
      const res = await reanalyze(subId);
      const result = res.data.data ?? res.data;
      setAnalysis(result);
      toast.success('Analysis complete.');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Re-analysis failed');
    } finally {
      setReanalyzing(false);
      setAnalysisLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <Spinner size="xl" className="text-primary-600" />
      </div>
    );
  }

  if (error || !participant) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface">
        <p className="text-sm text-error-500">{error ?? 'Participant not found'}</p>
        <button onClick={() => navigate(-1)} className="text-sm text-primary-600 underline">
          Go back
        </button>
      </div>
    );
  }

  const dimensionEntries = analysis?.scores ? Object.entries(analysis.scores) : [];
  const summary          = analysis?.summary ?? null;
  const keyInsights      = summary?.key_insights ?? [];

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">

      {/* ── Header ── */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate(-1)}
            className="mt-1 rounded-button p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-heading font-bold text-neutral-900">
                {participant.full_name ?? participant.name ?? 'Participant'}
              </h1>
              {submission?.status && (
                <Badge variant={statusVariant(submission.status)}>
                  {statusLabel(submission.status)}
                </Badge>
              )}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-500">
              {participant.startup_name && <span>{participant.startup_name}</span>}
              {participant.email        && <span>{participant.email}</span>}
            </div>
          </div>
        </div>

        {submission && (
          <button
            onClick={handleReanalyze}
            disabled={reanalyzing || analysisLoading}
            className="inline-flex shrink-0 items-center gap-2 rounded-button border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-button hover:bg-neutral-50 disabled:opacity-60 transition"
          >
            {reanalyzing
              ? <Spinner size="sm" className="text-neutral-500" />
              : <ArrowPathIcon className="h-4 w-4" />}
            {reanalyzing ? 'Analyzing…' : analysis ? 'Re-analyze' : 'Run Analysis'}
          </button>
        )}
      </div>

      {/* ── No submission yet ── */}
      {!submission ? (
        <Card>
          <div className="py-12 text-center">
            <UserCircleIcon className="mx-auto mb-3 h-12 w-12 text-neutral-300" />
            <p className="text-sm text-neutral-500">This participant has not submitted yet.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">

          {/* ── Top grid: scores + summary ── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

            {/* Left: Overall score + dimension breakdown */}
            <div className="space-y-6">
              <Card title="Overall Score">
                {analysisLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Spinner size="lg" className="text-primary-600" />
                  </div>
                ) : !analysis ? (
                  <div className="flex flex-col items-center gap-3 py-8">
                    <SparklesIcon className="h-10 w-10 text-neutral-300" />
                    <p className="text-sm text-neutral-500">No analysis yet</p>
                    <button
                      onClick={handleReanalyze}
                      disabled={reanalyzing}
                      className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60 transition"
                    >
                      {reanalyzing && <Spinner size="sm" className="text-white" />}
                      <SparklesIcon className="h-4 w-4" />
                      Run Analysis
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-2">
                    <ScoreGauge score={analysis.overall_score ?? 0} size={140} />
                    <div className="flex flex-col items-center gap-0.5">
                      {analysis.model_name && (
                        <p className="text-[10px] text-neutral-400">
                          Model: {analysis.model_name}
                        </p>
                      )}
                      {analysis.created_at && (
                        <p className="text-xs text-neutral-400">
                          Analyzed{' '}
                          {new Date(analysis.created_at).toLocaleDateString(undefined, {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </Card>

              {dimensionEntries.length > 0 && (
                <Card title="Dimension Scores">
                  <div className="space-y-4">
                    {dimensionEntries.map(([name, data]) => (
                      <DimensionRow key={name} name={name} data={data} />
                    ))}
                  </div>
                </Card>
              )}
            </div>

            {/* Right: AI summary, strengths, weaknesses, recommendation */}
            <div className="space-y-6">
              {summary ? (
                <>
                  {summary.overall_assessment && (
                    <Card title="AI Assessment">
                      <p className="text-sm text-neutral-700 leading-relaxed">
                        {summary.overall_assessment}
                      </p>
                    </Card>
                  )}

                  {summary.strengths?.length > 0 && (
                    <Card title="Strengths">
                      <ul className="space-y-2">
                        {summary.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-success-500" />
                            <span className="text-sm text-neutral-700">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {summary.weaknesses?.length > 0 && (
                    <Card title="Areas for Improvement">
                      <ul className="space-y-2">
                        {summary.weaknesses.map((w, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" />
                            <span className="text-sm text-neutral-700">{w}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {summary.recommendation && (
                    <div className="rounded-card border border-primary-200 bg-primary-50 p-5">
                      <div className="mb-2 flex items-center gap-2">
                        <LightBulbIcon className="h-5 w-5 text-primary-600" />
                        <span className="text-sm font-semibold text-primary-800">
                          Recommendation
                        </span>
                      </div>
                      <p className="text-sm text-primary-900 leading-relaxed">
                        {summary.recommendation}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                !analysisLoading && (
                  <Card title="AI Summary">
                    <div className="flex flex-col items-center gap-3 py-8">
                      <SparklesIcon className="h-10 w-10 text-neutral-300" />
                      <p className="text-sm text-neutral-500">
                        Run analysis to see AI insights here.
                      </p>
                    </div>
                  </Card>
                )
              )}
            </div>
          </div>

          {/* ── Key Insights (full width) ── */}
          {keyInsights.length > 0 && (
            <Card title="Key Insights">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {keyInsights.map((item, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 space-y-1"
                  >
                    <p className="text-sm font-medium text-neutral-800">{item.insight}</p>
                    {item.evidence && (
                      <p className="text-xs text-neutral-500 leading-relaxed">{item.evidence}</p>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* ── Q&A with per-question feedback ── */}
          {answers.length > 0 && (
            <Card
              title="Responses"
              subtitle={`${answers.length} answer${answers.length !== 1 ? 's' : ''}${
                analysis?.question_feedback?.length ? ' · AI feedback included' : ''
              }`}
            >
              <div>
                {answers.map((a, idx) => {
                  const qFeedback =
                    analysis?.question_feedback?.find((f) => f.question_number === idx + 1) ?? null;
                  return (
                    <QAItem
                      key={idx}
                      index={idx + 1}
                      question={a.question_text ?? a.question ?? `Question ${idx + 1}`}
                      answer={a.answer_text ?? a.answer ?? a.response ?? a.value}
                      feedback={qFeedback}
                    />
                  );
                })}
              </div>
            </Card>
          )}

        </div>
      )}
    </div>
  );
}
